import React, { useRef, useState } from "react";
import { Modal, Form, Select, Input, InputNumber, Radio, Switch, Typography, message } from "antd";
import { addPharmacyConfigApi } from "../../../services/pharmacyConfigs.services";
import { hsnRules } from "../common/validators";

const TAX_CODE_TYPE_OPTIONS = [
  { value: "HSN", label: "HSN" },
  { value: "SAC", label: "SAC" },
];

/*
 * A code carries its GST rate, and the rate can be given either way round: one
 * IGST figure for an inter-state sale, or the CGST/SGST pair it splits into
 * within a state. They are two ways of saying the same thing, so the form asks
 * which and sends only that - posting both would leave the server to decide
 * which one it believed.
 */
const GST_SPLIT = {
  IGST: "igst",
  PAIR: "pair",
};

const rateRules = (label) => [
  {
    validator: (_, value) => {
      if (value === undefined || value === null || value === "") return Promise.resolve();
      const rate = Number(value);
      if (!Number.isFinite(rate) || rate < 0 || rate > 100) {
        return Promise.reject(new Error(`${label} must be between 0 and 100`));
      }
      return Promise.resolve();
    },
  },
];

/** Blank means "not given" - 0 is a real rate and has to survive. */
const rate = (value) => (value === undefined || value === null || value === "" ? undefined : Number(value));

const given = (value) => value !== undefined && value !== null && value !== "";

/*
 * Within a state the tax is levied half by the centre and half by the state, so
 * a code whose CGST and SGST differ is not a rate anyone can charge - it is a
 * typo in one of the two boxes. Both are marked, because either one of them
 * could be the wrong one and the form cannot know which.
 */
const pairMatchRule = (form, sibling) => ({
  validator: (_, value) => {
    const other = form.getFieldValue(sibling);
    if (!given(value) || !given(other)) return Promise.resolve();
    if (Number(value) !== Number(other)) {
      return Promise.reject(new Error("CGST and SGST must be equal"));
    }
    return Promise.resolve();
  },
});

const HSNAndSACFormModal = ({ open, setOpen, onCreated }) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const gstSplit = Form.useWatch("gst_split", form) ?? GST_SPLIT.IGST;
  const cgst = Form.useWatch("cgst", form);
  const sgst = Form.useWatch("sgst", form);

  const closeAndReset = () => {
    form.resetFields();
    setOpen(false);
  };

  const onFinish = (values) => {
    const payload = {
      tax_code_type: values.tax_code_type,
      tax_code: values.tax_code,
      description: values.description || "",
      cess: rate(values.cess) ?? 0,
      is_active: values.is_active !== false,
    };

    if (values.gst_split === GST_SPLIT.PAIR) {
      payload.cgst = rate(values.cgst) ?? 0;
      payload.sgst = rate(values.sgst) ?? 0;
    } else {
      payload.igst = rate(values.igst) ?? 0;
    }

    setLoading(true);
    addPharmacyConfigApi("hsn-and-sac", payload)
      .then((res) => {
        message.success("HSN/SAC code added successfully");
        setLoading(false);
        closeAndReset();
        onCreated?.(res.data);
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || "Failed to save HSN/SAC code");
        setLoading(false);
      });
  };

  const pairTotal = (Number(cgst) || 0) + (Number(sgst) || 0);

  return (
    <Modal
      title="Add HSN/SAC Code"
      open={open}
      onCancel={closeAndReset}
      centered
      width={520}
      destroyOnClose
      okText="Save"
      confirmLoading={loading}
      onOk={() => formRef.current?.submit()}
    >
      <Form
        layout="vertical"
        form={form}
        ref={formRef}
        onFinish={onFinish}
        initialValues={{
          tax_code_type: "HSN",
          gst_split: GST_SPLIT.IGST,
          cess: 0,
          is_active: true,
        }}
      >
        <Form.Item
          label="Type"
          name="tax_code_type"
          rules={[{ required: true, message: "Please select a type" }]}
        >
          <Select options={TAX_CODE_TYPE_OPTIONS} />
        </Form.Item>

        <Form.Item
          label="Code"
          name="tax_code"
          rules={hsnRules({ required: true })}
        >
          <Input placeholder="e.g. 30049099" inputMode="numeric" maxLength={15} />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea
            rows={2}
            maxLength={255}
            placeholder="e.g. Medicaments consisting of mixed or unmixed products, put up in measured doses"
          />
        </Form.Item>

        <Form.Item label="GST rate given as" name="gst_split">
          <Radio.Group optionType="button" buttonStyle="solid">
            <Radio.Button value={GST_SPLIT.IGST}>IGST</Radio.Button>
            <Radio.Button value={GST_SPLIT.PAIR}>CGST + SGST</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {gstSplit === GST_SPLIT.PAIR ? (
          <>
            {/*
              * The two boxes carry no bottom margin of their own, and the
              * total underneath supplies the gap instead.
              *
              * antd draws a field's error into the margin below it rather
              * than growing the row - it measures that margin and cancels it
              * again with an offset of the same size. A caption tucked up
              * into that margin is therefore sitting exactly where the error
              * will be written, which is how "CGST and SGST must be equal"
              * came to be printed through "Total GST 15%". With no margin to
              * write into, the error takes room of its own and pushes the
              * total down ahead of it.
              */}
            <div style={{ display: "flex", gap: 12 }}>
              <Form.Item
                label="CGST %"
                name="cgst"
                rules={[...rateRules("CGST"), pairMatchRule(form, "sgst")]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={0.5}
                  style={{ width: "100%" }}
                  placeholder="2.5"
                  /* The other box is what this one is being judged against, so
                     it is re-checked here - otherwise a stale error stays on it
                     after the mismatch has been typed away. */
                  onChange={() => form.validateFields(["sgst"]).catch(() => {})}
                />
              </Form.Item>
              <Form.Item
                label="SGST %"
                name="sgst"
                rules={[...rateRules("SGST"), pairMatchRule(form, "cgst")]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={0.5}
                  style={{ width: "100%" }}
                  placeholder="2.5"
                  onChange={() => form.validateFields(["cgst"]).catch(() => {})}
                />
              </Form.Item>
            </div>
            <Typography.Text
              type="secondary"
              style={{ display: "block", marginTop: 6, marginBottom: 24, fontSize: 12 }}
            >
              Total GST {pairTotal}%
            </Typography.Text>
          </>
        ) : (
          <Form.Item label="IGST %" name="igst" rules={rateRules("IGST")}>
            <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} placeholder="5" />
          </Form.Item>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <Form.Item label="Cess %" name="cess" rules={rateRules("Cess")} style={{ flex: 1 }}>
            <InputNumber min={0} max={100} step={0.5} style={{ width: "100%" }} placeholder="0" />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked" style={{ flex: 1 }}>
            <Switch />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default HSNAndSACFormModal;
